/* eslint-disable max-lines */
import { useTu } from "@src/appV2/i18n/useTu";
import { type WorkplaceProfileResponse } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { EMPLOYEE_ROLES } from "@src/appV2/Employee/constants";
import { useUpdateInstantBook } from "@src/appV2/FacilityDrawer/InstantBook/api/useUpdateInstantBook";
import { type UseGetFacilityProfileResponse } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { useUpdateFacilityProfile } from "@src/appV2/FacilityProfile/api/useUpdateFacilityProfile";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useToast } from "@src/appV2/lib";
import { useDeprecatedDispatch } from "@src/appV2/lib/utils/useDeprecatedDispatch";
import { useState } from "react";

import { useLateCancellationCallNotification } from "../../hooks/useLateCancellationCallNotification";
import { useShiftBookedChatNotification } from "../../hooks/useShiftBookedChatNotification";
import { useUpdatePreShiftOrientationSetting } from "../../hooks/useUpdatePreShiftOrientationSetting";
import { type SettingsPermissions } from "../../types";
import { SchedulingSectionView } from "./SchedulingSectionView";

const CONSECUTIVE_HOURS_LIMIT_MINIMUM = 8;
const CONSECUTIVE_HOURS_LIMIT_DEFAULT = 8;
const WEEKLY_WORK_HOURS_LIMIT_MINIMUM = 8;
const WEEKLY_WORK_HOURS_LIMIT_DEFAULT = 40;
const WEEKLY_WORK_HOURS_LIMIT_MAXIMUM = 168;
const REQUEST_TO_BOOK_LAST_MINUTE_HOURS_THRESHOLD_MINIMUM = 1;
const REQUEST_TO_BOOK_DEFAULT_LAST_MINUTE_HOURS_THRESHOLD = 2;

interface SchedulingSectionContainerProps {
  facilityProfile: UseGetFacilityProfileResponse | undefined;
  guardSettingsAction: (action: () => void) => void;
  hasAdminRole: (role: string) => boolean;
  permissions: SettingsPermissions;
  workplace: WorkplaceProfileResponse | undefined;
  workplaceId: string;
  onSettingsChanged: () => void;
}

export function SchedulingSectionContainer({
  facilityProfile,
  guardSettingsAction,
  hasAdminRole,
  permissions,
  workplace,
  workplaceId,
  onSettingsChanged,
}: SchedulingSectionContainerProps) {
  const { tu } = useTu("settings");

  const { showSuccessToast, showErrorToast } = useToast();
  const { profile } = useSession();
  const { deprecatedDispatch } = useDeprecatedDispatch();

  const shouldRebrandToNoDoubles = useCbhFlag(
    CbhFeatureFlag.REBRAND_MAX_CONSECUTIVE_HOURS_TO_NO_DOUBLES,
    { defaultValue: false }
  );

  const hasRequestToBookPermission = hasAdminRole(EMPLOYEE_ROLES.REQUEST_TO_BOOK);
  const canManageSchedule =
    permissions.isAdmin || permissions.isFacilityAdmin || permissions.hasShiftManagement;

  const workplaceAttributes = workplace?.data?.attributes;
  const isRequestToBookEnabled = workplaceAttributes?.instantBook === "REQUEST_TO_BOOK";

  const [maxConsecutiveHours, setMaxConsecutiveHours] = useState<number | null>(
    facilityProfile?.maxAllowedWorkConsecutiveHours ?? null
  );
  const [maxWeeklyHours, setMaxWeeklyHours] = useState<number | null>(
    facilityProfile?.maxAllowedWorkHoursPerWeek ?? null
  );
  const [requestToBookEnabled, setRequestToBookEnabled] = useState(isRequestToBookEnabled);
  const [autoApprovePreviousApprovedWorkersEnabled, setAutoApprovePreviousApprovedWorkersEnabled] =
    useState(workplaceAttributes?.requestToBook?.autoApprovePreviousApprovedWorkers ?? false);
  const [autoApprovePastWorkersEnabled, setAutoApprovePastWorkersEnabled] = useState(
    workplaceAttributes?.requestToBook?.autoApprovePastWorkers ?? false
  );
  const [lastMinuteThresholdHours, setLastMinuteHoursThreshold] = useState<number | null>(
    workplaceAttributes?.requestToBook?.lastMinuteThresholdHours ?? null
  );

  const {
    editModalState: preShiftOrientationEditModalState,
    enabled: preShiftOrientationEnabled,
    handleSave: handlePreShiftOrientationSave,
    handleToggle: handlePreShiftOrientationToggle,
    isUpdating: isPreShiftOrientationUpdating,
  } = useUpdatePreShiftOrientationSetting({
    workplace,
    onSettingsChanged,
  });

  const preShiftOrientationConfig = workplaceAttributes?.preShiftOrientation;

  const { isLoading: isFacilityProfileUpdating, mutateAsync: updateFacilityProfile } =
    useUpdateFacilityProfile();
  const { isLoading: isInstantBookUpdating, mutateAsync: updateInstantBook } =
    useUpdateInstantBook();

  const lateCancellationCallNotification = useLateCancellationCallNotification({
    workplace,
    onSettingsChanged,
  });

  const shiftBookedChatNotification = useShiftBookedChatNotification({
    workplace,
    onSettingsChanged,
  });

  const isUpdating =
    isFacilityProfileUpdating ||
    isInstantBookUpdating ||
    isPreShiftOrientationUpdating ||
    lateCancellationCallNotification.isUpdating ||
    shiftBookedChatNotification.isUpdating;

  const getConsecutiveHoursDefault = () =>
    shouldRebrandToNoDoubles ? 1 : CONSECUTIVE_HOURS_LIMIT_DEFAULT;

  const handleMaxConsecutiveHoursToggle = async (checked: boolean) => {
    const hours = checked ? getConsecutiveHoursDefault() : null;
    setMaxConsecutiveHours(hours);
    try {
      await updateFacilityProfile({
        facilityId: workplaceId,
        maxAllowedWorkConsecutiveHours: hours,
      });
      showSuccessToast(tu("Schedule preferences updated successfully"));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Error updating workplace's profile. Please try again later."));
    }
  };

  const handleMaxConsecutiveHoursChange = async (value: number) => {
    if (value < CONSECUTIVE_HOURS_LIMIT_MINIMUM && !shouldRebrandToNoDoubles) {
      showErrorToast(
        tu("Restrictions must be no less than {{minimumHours}} hours", {
          values: { minimumHours: CONSECUTIVE_HOURS_LIMIT_MINIMUM },
        })
      );
      return;
    }
    setMaxConsecutiveHours(value);
    try {
      await updateFacilityProfile({
        facilityId: workplaceId,
        maxAllowedWorkConsecutiveHours: value,
      });
      showSuccessToast(tu("Schedule preferences updated successfully"));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Error updating workplace's profile. Please try again later."));
    }
  };

  const handleMaxWeeklyHoursToggle = async (checked: boolean) => {
    const hours = checked ? WEEKLY_WORK_HOURS_LIMIT_DEFAULT : null;
    setMaxWeeklyHours(hours);
    try {
      await updateFacilityProfile({
        facilityId: workplaceId,
        maxAllowedWorkHoursPerWeek: hours,
      });
      showSuccessToast(tu("Schedule preferences updated successfully"));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Error updating workplace's profile. Please try again later."));
    }
  };

  const handleMaxWeeklyHoursChange = async (value: number) => {
    if (value < WEEKLY_WORK_HOURS_LIMIT_MINIMUM) {
      showErrorToast(
        tu("Weekly hours limit must be no less than {{minimumHours}} hours", {
          values: { minimumHours: WEEKLY_WORK_HOURS_LIMIT_MINIMUM },
        })
      );
      return;
    }
    if (value > WEEKLY_WORK_HOURS_LIMIT_MAXIMUM) {
      showErrorToast(
        tu("Weekly hours limit must be no more than {{maximumHours}} hours", {
          values: { maximumHours: WEEKLY_WORK_HOURS_LIMIT_MAXIMUM },
        })
      );
      return;
    }
    setMaxWeeklyHours(value);
    try {
      await updateFacilityProfile({
        facilityId: workplaceId,
        maxAllowedWorkHoursPerWeek: value,
      });
      showSuccessToast(tu("Schedule preferences updated successfully"));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Error updating workplace's profile. Please try again later."));
    }
  };

  const handleRequestToBookToggle = async (checked: boolean) => {
    setRequestToBookEnabled(checked);
    // The backend clears the persisted threshold whenever instantBook moves away
    // from REQUEST_TO_BOOK, so reset local state and stop sending the stale value
    // on disable. This keeps the threshold field empty if R2B is re-enabled.
    if (!checked) {
      setLastMinuteHoursThreshold(null);
    }
    try {
      await updateInstantBook({
        workplaceId,
        data: {
          instantBook: checked ? "REQUEST_TO_BOOK" : "ON",
          requestToBook: {
            autoApprovePreviousApprovedWorkers: checked
              ? autoApprovePreviousApprovedWorkersEnabled
              : false,
            autoApprovePastWorkers: checked ? autoApprovePastWorkersEnabled : false,
            qualifications: workplaceAttributes?.requestToBook?.qualifications ?? [],
            shiftTimeSlots: workplaceAttributes?.requestToBook?.shiftTimeSlots ?? [],
            lastMinuteThresholdHours: checked ? (lastMinuteThresholdHours ?? undefined) : undefined,
          },
        },
      });
      showSuccessToast(tu("Request to Book settings updated successfully"));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Error updating workplace's profile. Please try again later."));
    }
  };

  const handleAutoApprovePreviousApprovedWorkersToggle = async (checked: boolean) => {
    setAutoApprovePreviousApprovedWorkersEnabled(checked);
    try {
      await updateInstantBook({
        workplaceId,
        data: {
          instantBook: "REQUEST_TO_BOOK",
          requestToBook: {
            autoApprovePreviousApprovedWorkers: checked,
            autoApprovePastWorkers: autoApprovePastWorkersEnabled,
            qualifications: workplaceAttributes?.requestToBook?.qualifications ?? [],
            shiftTimeSlots: workplaceAttributes?.requestToBook?.shiftTimeSlots ?? [],
            lastMinuteThresholdHours: lastMinuteThresholdHours ?? undefined,
          },
        },
      });
      showSuccessToast(tu("Auto-approve settings updated successfully"));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Error updating workplace's profile. Please try again later."));
    }
  };

  const handleAutoApprovePastWorkersToggle = async (checked: boolean) => {
    setAutoApprovePastWorkersEnabled(checked);
    try {
      await updateInstantBook({
        workplaceId,
        data: {
          instantBook: "REQUEST_TO_BOOK",
          requestToBook: {
            autoApprovePreviousApprovedWorkers: autoApprovePreviousApprovedWorkersEnabled,
            autoApprovePastWorkers: checked,
            qualifications: workplaceAttributes?.requestToBook?.qualifications ?? [],
            shiftTimeSlots: workplaceAttributes?.requestToBook?.shiftTimeSlots ?? [],
            lastMinuteThresholdHours: lastMinuteThresholdHours ?? undefined,
          },
        },
      });
      showSuccessToast(tu("Auto-approve past workers setting updated successfully"));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Error updating workplace's profile. Please try again later."));
    }
  };

  const handleLastMinuteHoursToggle = async (checked: boolean) => {
    const threshold = checked ? REQUEST_TO_BOOK_DEFAULT_LAST_MINUTE_HOURS_THRESHOLD : null;
    setLastMinuteHoursThreshold(threshold);
    const requestToBook = {
      autoApprovePreviousApprovedWorkers: autoApprovePreviousApprovedWorkersEnabled,
      autoApprovePastWorkers: autoApprovePastWorkersEnabled,
      qualifications: workplaceAttributes?.requestToBook?.qualifications ?? [],
      shiftTimeSlots: workplaceAttributes?.requestToBook?.shiftTimeSlots ?? [],
      lastMinuteThresholdHours: threshold ?? undefined,
    };
    try {
      await updateInstantBook({
        workplaceId,
        data: { instantBook: "REQUEST_TO_BOOK", requestToBook },
      });
      // Keep the Redux session profile in sync so shift-posting forms (which read
      // requestToBook from the session) reflect the change without a page reload.
      // Only touch lastMinuteThresholdHours — every other profile/requestToBook field
      // is preserved as-is so this dispatch changes nothing we don't intend.
      deprecatedDispatch({
        type: "session/UPDATE_PROFILE",
        data: {
          ...profile,
          requestToBook: {
            ...profile.requestToBook,
            lastMinuteThresholdHours: threshold ?? undefined,
          },
        },
      });
      showSuccessToast(tu("Request to Book threshold hours updated successfully"));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Error updating workplace's profile. Please try again later."));
    }
  };

  const handleLastMinuteHoursChange = async (value: number) => {
    if (value < REQUEST_TO_BOOK_LAST_MINUTE_HOURS_THRESHOLD_MINIMUM) {
      showErrorToast(
        tu("Threshold must be no less than {{minimumHours}} hours", {
          values: { minimumHours: REQUEST_TO_BOOK_LAST_MINUTE_HOURS_THRESHOLD_MINIMUM },
        })
      );
      return;
    }
    setLastMinuteHoursThreshold(value);
    const requestToBook = {
      autoApprovePreviousApprovedWorkers: autoApprovePreviousApprovedWorkersEnabled,
      autoApprovePastWorkers: autoApprovePastWorkersEnabled,
      qualifications: workplaceAttributes?.requestToBook?.qualifications ?? [],
      shiftTimeSlots: workplaceAttributes?.requestToBook?.shiftTimeSlots ?? [],
      lastMinuteThresholdHours: value,
    };
    try {
      await updateInstantBook({
        workplaceId,
        data: { instantBook: "REQUEST_TO_BOOK", requestToBook },
      });
      deprecatedDispatch({
        type: "session/UPDATE_PROFILE",
        data: {
          ...profile,
          requestToBook: { ...profile.requestToBook, lastMinuteThresholdHours: value },
        },
      });
      showSuccessToast(tu("Request to Book threshold hours updated successfully"));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Error updating workplace's profile. Please try again later."));
    }
  };

  if (!canManageSchedule) {
    return null;
  }

  return (
    <SchedulingSectionView
      autoApprovePastWorkersEnabled={autoApprovePastWorkersEnabled}
      autoApprovePreviousApprovedWorkersEnabled={autoApprovePreviousApprovedWorkersEnabled}
      isLateCancellationCallNotificationEnabled={lateCancellationCallNotification.enabled}
      isRequestToBookEnabled={requestToBookEnabled}
      isShiftBookedChatNotificationEnabled={shiftBookedChatNotification.enabled}
      isUpdating={isUpdating}
      lateCancellationCallInstructions={lateCancellationCallNotification.instructionsText}
      lateCancellationCallNotificationModalState={
        lateCancellationCallNotification.editInstructionsModalState
      }
      maxConsecutiveHours={maxConsecutiveHours}
      maxConsecutiveHoursEnabled={isDefined(maxConsecutiveHours)}
      maxWeeklyHours={maxWeeklyHours}
      maxWeeklyHoursEnabled={isDefined(maxWeeklyHours)}
      permissions={permissions}
      preShiftOrientationEnabled={preShiftOrientationEnabled}
      preShiftOrientationLengthInMinutes={preShiftOrientationConfig?.orientationLengthInMinutes}
      preShiftOrientationModalState={preShiftOrientationEditModalState}
      preShiftOrientationQualifications={preShiftOrientationConfig?.qualifications}
      requestToBookDisabled={!hasRequestToBookPermission}
      workplaceId={workplaceId}
      workplaceTypeName={workplaceAttributes?.type}
      requestToBookSubtext={
        !permissions.isAdmin || !hasRequestToBookPermission
          ? tu(
              "To modify the workplace's Request to Book setting, submit a request in #proj-request-to-book"
            )
          : undefined
      }
      shouldRebrandToNoDoubles={shouldRebrandToNoDoubles}
      onAutoApprovePastWorkersToggle={(checked) => {
        guardSettingsAction(() => {
          void handleAutoApprovePastWorkersToggle(checked);
        });
      }}
      onAutoApprovePreviousApprovedWorkersToggle={(checked) => {
        guardSettingsAction(() => {
          void handleAutoApprovePreviousApprovedWorkersToggle(checked);
        });
      }}
      onLateCancellationCallInstructionsSave={
        lateCancellationCallNotification.handleInstructionsSave
      }
      onLateCancellationCallNotificationToggle={(checked) => {
        guardSettingsAction(() => {
          void lateCancellationCallNotification.handleToggle(checked);
        });
      }}
      onMaxConsecutiveHoursChange={(value) => {
        guardSettingsAction(() => {
          void handleMaxConsecutiveHoursChange(value);
        });
      }}
      onMaxConsecutiveHoursToggle={(checked) => {
        guardSettingsAction(() => {
          void handleMaxConsecutiveHoursToggle(checked);
        });
      }}
      onMaxWeeklyHoursChange={(value) => {
        guardSettingsAction(() => {
          void handleMaxWeeklyHoursChange(value);
        });
      }}
      onMaxWeeklyHoursToggle={(checked) => {
        guardSettingsAction(() => {
          void handleMaxWeeklyHoursToggle(checked);
        });
      }}
      onPreShiftOrientationSave={(values) => {
        guardSettingsAction(() => {
          void handlePreShiftOrientationSave(values);
        });
      }}
      onPreShiftOrientationToggle={(checked) => {
        guardSettingsAction(() => {
          void handlePreShiftOrientationToggle(checked);
        });
      }}
      onRequestToBookToggle={(checked) => {
        guardSettingsAction(() => {
          void handleRequestToBookToggle(checked);
        });
      }}
      onShiftBookedChatNotificationToggle={(checked) => {
        guardSettingsAction(() => {
          void shiftBookedChatNotification.handleToggle(checked);
        });
      }}
      lastMinuteThresholdHours={lastMinuteThresholdHours}
      onLastMinuteHoursToggle={(checked) => {
        guardSettingsAction(() => {
          void handleLastMinuteHoursToggle(checked);
        });
      }}
      onLastMinuteHoursChange={(value) => {
        guardSettingsAction(() => {
          void handleLastMinuteHoursChange(value);
        });
      }}
    />
  );
}
/* eslint-enable max-lines */
